import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { List, ListItem, Typography } from "@mui/material";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { DialogModal } from "Components/_Shared/DialogModal";
import { useValidateAnalysisContractCreation } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { NotFoundError, UnprocessableEntityError } from "Infrastructure/ApiZfp/Schema";
import { Configuration } from "Infrastructure/Config/config";
import { FeatureFlag, useFeatureFlag } from "Infrastructure/Hooks/useFeatureFlag";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useNsureServerError, useResource } from "Infrastructure/Translations/Resources";
import { type FC, useContext, useState } from "react";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { GoalFulfillmentJustification } from "./GoalFulfillmentJustification";
import { useGoalsFulfillmentContext } from "./GoalsFulfillmentContext";

type Props = {
  disabled: boolean;
};

export const GoalFulfillmentContractingButton: FC<Props> = ({ disabled }) => {
  const { t } = useResource();
  const { analysisId, versionId, readOnly } = useContext(AnalysisDetailContext);
  const { translateError } = useNsureServerError(Resources.Errors.ApplicationError.Title);
  const isDevelopment = Configuration.getIsDevelopment();
  const contractingEnabled = useFeatureFlag(FeatureFlag.GrowUpAllowContracting);
  const { hasAnyEvaluationWarning, hasAnyEvaluationPending } = useGoalsFulfillmentContext();

  const [justificationOpen, setJustificationOpen] = useState(false);
  const [validationError, setValidationError] = useState<NotFoundError | UnprocessableEntityError | null>(null);

  const { refetch: validateContractCreation, isFetching: isValidating } = useValidateAnalysisContractCreation(analysisId, versionId, {
    query: { enabled: false, retry: 1 },
  });

  const handleClick = async () => {
    const result = await validateContractCreation();
    if (result.status === "success") {
      setJustificationOpen(true);
    } else if (result.error) {
      setValidationError(result.error as NotFoundError | UnprocessableEntityError);
    }
  };

  return (
    <>
      <Button
        disabled={contractingEnabled && isDevelopment ? false : disabled || hasAnyEvaluationWarning || hasAnyEvaluationPending || readOnly}
        loading={isValidating}
        onClick={handleClick}
      >
        {t(Resources.AnalysisDetail.GoalsFulfillment.ToContractingGrowUp)}
      </Button>

      <GoalFulfillmentJustification isOpen={justificationOpen} onClose={() => setJustificationOpen(false)} />

      <DialogModal
        isOpen={!!validationError}
        onClose={() => setValidationError(null)}
        title={t(Resources.AnalysisDetail.GoalsFulfillment.ContractValidation.ErrorTitle)}
        subtitle={t(Resources.AnalysisDetail.GoalsFulfillment.ContractValidation.ErrorSubtitle)}
        content={
          validationError &&
          ("errors" in validationError ? (
            <List dense disablePadding>
              {validationError.errors.map((err) => (
                <ListItem key={`${err.code ?? "unknown"}-${Math.random()}`} disableGutters>
                  <Flex alignItems="center" gap={Spacing.L}>
                    <WarningAmberOutlinedIcon fontSize="small" sx={{ color: "error.dark" }} />
                    <Typography variant="body2">{err.code ? translateError(err.code, err.messageArgs) : err.message}</Typography>
                  </Flex>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">{validationError.code}</Typography>
          ))
        }
        primaryAction={null}
        secondaryAction={
          isDevelopment ? (
            <Button
              onClick={() => {
                setValidationError(null);
                setJustificationOpen(true);
              }}
            >
              [TEST] Skip
            </Button>
          ) : null
        }
      />
    </>
  );
};
